import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";
import {
  Info,
  FileText,
  Package,
  ListChecks,
  ImageIcon,
  AlertCircle,
  Building2,
  type LucideIcon,
} from "lucide-react";
import { getVendorInitials } from "@/features/marketplace/lib/vendorInitials";
import type { SectionKey } from "../lib/admin-vendor-form.utils";

interface FormNavRailProps {
  logoUrl: string | null;
  name: string;
  slug: string;
  isActive: boolean;
  isEdit: boolean;
  activeSection: SectionKey;
  packagesCount: number;
  questionsCount: number;
  sectionErrors: Record<SectionKey, boolean>;
  sectionDirty?: Record<SectionKey, boolean>;
  showPartnerSection?: boolean;
  onNavigate: (key: SectionKey) => void;
}

/**
 * Sticky left rail (lg+): a live identity preview of the vendor being edited
 * plus quick-jump navigation that mirrors the form's steps.
 */
export function FormNavRail({
  logoUrl,
  name,
  slug,
  isActive,
  isEdit,
  activeSection,
  packagesCount,
  questionsCount,
  sectionErrors,
  sectionDirty,
  showPartnerSection = false,
  onNavigate,
}: FormNavRailProps) {
  const { t } = useTranslation();

  const items: { key: SectionKey; label: string; icon: LucideIcon; count?: number }[] = [
    { key: "basics", label: t("marketplace.admin.sections.basics"), icon: Info },
    ...(showPartnerSection
      ? [{
          key: "partner" as const,
          label: t("marketplace.admin.partner.section", "Partner org"),
          icon: Building2,
        }]
      : []),
    { key: "agreement", label: t("marketplace.admin.sections.agreement"), icon: FileText },
    {
      key: "packages",
      label: t("marketplace.admin.form.packages"),
      icon: Package,
      count: packagesCount,
    },
    {
      key: "questions",
      label: t("marketplace.admin.form.questions"),
      icon: ListChecks,
      count: questionsCount,
    },
  ];

  return (
    <aside className="sticky top-0 hidden lg:block">
      <div className="flex items-center gap-3">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            aria-hidden
            className="h-11 w-11 shrink-0 rounded-lg border border-border/60 bg-background object-contain"
          />
        ) : (
          <div
            aria-hidden
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-muted text-sm font-semibold text-muted-foreground"
          >
            {name ? getVendorInitials(name) : <ImageIcon className="h-5 w-5 opacity-40" />}
          </div>
        )}
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold tracking-tight">
            {name || t("marketplace.admin.form.untitledVendor", { defaultValue: "New partner" })}
          </p>
          <p className="truncate font-mono text-xs text-muted-foreground">{slug || "—"}</p>
        </div>
      </div>

      {isEdit && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <span
            aria-hidden
            className={cn(
              "h-1.5 w-1.5 rounded-full",
              isActive ? "bg-status-success" : "bg-muted-foreground/40",
            )}
          />
          {isActive
            ? t("marketplace.admin.status.active")
            : t("marketplace.admin.status.inactive")}
        </div>
      )}

      <nav className="mt-5 flex flex-col gap-0.5 border-t border-border/60 pt-4">
        {items.map(({ key, label, icon: Icon, count }) => {
          const active = activeSection === key;
          const hasError = sectionErrors[key];
          const isDirty = Boolean(sectionDirty?.[key]);
          return (
            <button
              key={key}
              type="button"
              onClick={() => onNavigate(key)}
              aria-current={active ? "true" : undefined}
              className={cn(
                "group flex items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm transition-colors duration-150 ease-out",
                hasError
                  ? cn(
                      "text-destructive hover:bg-destructive/10",
                      active && "bg-destructive/10 font-medium",
                    )
                  : active
                    ? "bg-primary/10 font-medium text-primary"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <Icon
                className={cn(
                  "h-4 w-4 shrink-0",
                  hasError
                    ? "text-destructive"
                    : active
                      ? "text-primary"
                      : "text-muted-foreground/70",
                )}
                aria-hidden
              />
              <span className="flex-1 truncate">{label}</span>
              <span className="flex shrink-0 items-center gap-1.5">
                {isDirty && !hasError ? (
                  <>
                    <span className="sr-only">
                      {t("marketplace.admin.form.sectionUnsaved", "unsaved changes")}
                    </span>
                    <span
                      aria-hidden
                      className="h-1.5 w-1.5 rounded-full bg-current opacity-80"
                    />
                  </>
                ) : null}
                {hasError ? (
                  <AlertCircle
                    className="h-3.5 w-3.5 shrink-0 text-destructive"
                    aria-label={t("marketplace.admin.form.sectionHasError", {
                      defaultValue: "This section needs attention",
                    })}
                  />
                ) : (
                  count != null &&
                  count > 0 && (
                    <span className="text-xs tabular-nums text-muted-foreground/70">
                      {count}
                    </span>
                  )
                )}
              </span>
            </button>
          );
        })}
      </nav>
    </aside>
  );
}
